// dd-data.js — Assessment Deep Dive (section 02) data model.
// Ported from the Claude Design source `dd-data.jsx` (V2). Band thresholds run
// along the displayed 0–100 score axis and are tuned so each group's score lands
// in its named band. For most assessments higher = healthier; VBI (Burnout) is RAW
// (higher = worse), flagged with higherWorse:true so colouring/marker direction stay
// correct.
//
// `assessmentId` is on every entry so live backend data (keyed by the DB
// `assessment_id`) merges onto the design dataset at runtime — the dd string ids
// (isi/gad/phq/…) differ from the Section 1 `data.js` ids.
//
// V2 note: assessments are surfaced under friendly "Indicator" names; the headline
// ribbon `bands` use the V2 neon palette. The population `distBands`/`distHigherWorse`
// and `dims` still drive the LIVE (backend) distribution/dimension merge and are
// unchanged. Moral Injury / Capability / Follow-Up + the program journey are not
// shown in V2, so they are omitted here (their backend queries are untouched).

// tone for band index i of n, given direction
export function ddBandTone(i, n, higherWorse) {
  const f = higherWorse ? 1 - i / (n - 1) : i / (n - 1); // health fraction
  if (f < 0.34) return "risk";
  if (f < 0.67) return "warning";
  return "good";
}
export function ddToneColor(t) {
  return t === "good" ? "#4AE07A" : t === "risk" ? "#FF5C57" : "#FFD84A";
}
export function ddDimTone(v) {
  return v < 50 ? "risk" : v < 67 ? "warning" : "good";
}

// ===== Neon pastel color system (V2) =====
// System A — Organizational Readiness Index + Readiness Indicator (5 bands, worst→best)
export const NEON_A = ["#FF5C57", "#FF8C42", "#4AE07A", "#45B4FF", "#C670F0"];
// System B — every other scored assessment (4 tiers, severe→healthy)
export const NEON_B = ["#FF5C57", "#FF8C42", "#FFD84A", "#4AE07A"];
// Readiness (system A) thresholds, mirrors READINESS_BANDS in data.js. Kept local
// to avoid a circular import between the Section-1 and Section-2 data modules.
const READINESS_STOPS = [
  { max: 39, color: NEON_A[0] }, { max: 59, color: NEON_A[1] },
  { max: 74, color: NEON_A[2] }, { max: 89, color: NEON_A[3] }, { max: 100, color: NEON_A[4] },
];

export function ddSystemFor(id) {
  return id === "wellness" ? "A" : "B";
}

// color for band index i of n, given health direction and color system
export function ddBandColor(i, n, higherWorse, system) {
  const f = n <= 1 ? 1 : (higherWorse ? 1 - i / (n - 1) : i / (n - 1)); // 0 = worst … 1 = healthiest
  if (system === "A") return NEON_A[Math.round(f * (NEON_A.length - 1))];
  return NEON_B[Math.min(NEON_B.length - 1, Math.floor(f * NEON_B.length))];
}

// color for a 0–100 health-aligned score (higher = healthier)
export function ddScoreColor(v, system) {
  if (system === "A") return (READINESS_STOPS.find((s) => v <= s.max) || READINESS_STOPS[0]).color;
  return v < 40 ? NEON_B[0] : v < 60 ? NEON_B[1] : v < 75 ? NEON_B[2] : NEON_B[3];
}

const Q4 = ["Q3 ’25", "Q4 ’25", "Q1 ’26", "Q2 ’26"];

export const DD = {
  order: ["vasana", "wellness", "isi", "gad", "phq", "pcl", "mspss", "audit", "proqol", "vbi"],
  data: {
    vasana: {
      id: "vasana", assessmentId: 1, tab: "Vasana Type", name: "Vasana Type",
      categorical: true,
      measures: "Your organization's biological response under pressure, described across four types: Sa, Re, Ga, Ma",
      recall: "Onboarding", items: 0, n: 342, part: 83,
      dominant: "RE",
      mix: [
        { type: "RE", pct: 31 }, { type: "GA", pct: 27 },
        { type: "SA", pct: 24 }, { type: "MA", pct: 18 },
      ],
    },

    wellness: {
      id: "wellness", assessmentId: 30, tab: "Readiness Indicator", name: "Readiness Indicator",
      measures: "An aggregate readiness read across eight dimensions that shape how your people perform and recover.",
      recall: "Onboarding", items: 8, n: 354, part: 86,
      score: 64, band: "Steady", trend: { dir: "up", value: 2, improving: true },
      bands: [
        { label: "Strained", lo: 0, hi: 30 }, { label: "Needs Attention", lo: 30, hi: 50 },
        { label: "Steady", lo: 50, hi: 68 }, { label: "Strong", lo: 68, hi: 85 },
        { label: "Thriving", lo: 85, hi: 100 },
      ],
      dist: [
        { label: "Low", pct: 9 }, { label: "Moderate", pct: 22 },
        { label: "High", pct: 38 }, { label: "Strong", pct: 24 }, { label: "Robust", pct: 7 },
      ],
      // The population distribution uses its own 5-band labels (Low→Robust,
      // health-aligned), distinct from the ribbon `bands` names — so it needs
      // its own colored bands (readiness ramp: red→purple ascending).
      distBands: [
        { label: "Low", color: "#FF5C57" },
        { label: "Moderate", color: "#FF8C42" },
        { label: "High", color: "#4AE07A" },
        { label: "Strong", color: "#45B4FF" },
        { label: "Robust", color: "#C670F0" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Sleep", score: 48 }, { label: "Nutrition", score: 61 },
        { label: "Hydration", score: 70 }, { label: "Movement", score: 66 },
        { label: "Stress & Anxiety", score: 52 }, { label: "Energy", score: 55 },
        { label: "Connection", score: 73 }, { label: "Self Care", score: 58 },
      ],
      module: "wellness",
      weights: [
        { label: "Sleep", w: 0.18, score: 48 }, { label: "Energy", w: 0.18, score: 55 },
        { label: "Stress & Anxiety", w: 0.14, score: 52 }, { label: "Nutrition", w: 0.12, score: 61 },
        { label: "Movement", w: 0.12, score: 66 }, { label: "Connection", w: 0.10, score: 73 },
        { label: "Hydration", w: 0.08, score: 70 }, { label: "Self Care", w: 0.08, score: 58 },
      ],
      waves: Q4.map((l, i) => ({ label: l, score: [60, 61, 62, 64][i] })),
    },

    isi: {
      id: "isi", assessmentId: 4, tab: "Sleep Quality Indicator", name: "Sleep Quality Indicator",
      measures: "How much sleep difficulty is affecting people — trouble falling asleep, staying asleep, and the daytime cost of poor sleep.",
      recall: "Last 2 weeks", items: 7, n: 325, part: 79,
      score: 52, band: "Sub-threshold", trend: { dir: "down", value: 1, improving: false },
      bands: [
        { label: "Severe", lo: 0, hi: 25, color: "#FF5C57" }, { label: "Moderate", lo: 25, hi: 50, color: "#FF8C42" },
        { label: "Sub-threshold", lo: 50, hi: 75, color: "#FFD84A" }, { label: "None", lo: 75, hi: 100, color: "#4AE07A" },
      ],
      dist: [
        { label: "None", pct: 19 },
        { label: "Sub-threshold", pct: 38 },
        { label: "Moderate", pct: 29 },
        { label: "Severe", pct: 14 },
      ],
      // Population band distribution is bucketed on the RAW ISI score (clinical
      // cutoffs <8 / <15 / <22 / else), best→worst, so it uses its own band set
      // for tone — none = good, severe = risk — independent of the headline ribbon.
      distBands: [
        { label: "Severe" },
        { label: "Moderate" },
        { label: "Sub-threshold" },
        { label: "None" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Sleep Onset", score: 50 }, { label: "Sleep Maintenance", score: 47 },
        { label: "Daytime Impact", score: 55 },
      ],
      module: null,
      waves: Q4.map((l, i) => ({ label: l, score: [56, 55, 53, 52][i] })),
    },

    gad: {
      id: "gad", assessmentId: 9, tab: "Anxiety Severity Indicator", name: "Anxiety Severity Indicator",
      measures: "General anxiety load — how much worry, tension, and restlessness people are carrying day to day.",
      recall: "Last 2 weeks", items: 7, n: 330, part: 80,
      score: 61, band: "Mild", trend: { dir: "up", value: 1, improving: true },
      bands: [
        { label: "High", lo: 0, hi: 33, color: "#FF5C57" }, { label: "Moderate", lo: 33, hi: 57, color: "#FF8C42" },
        { label: "Mild", lo: 57, hi: 81, color: "#FFD84A" }, { label: "Low", lo: 81, hi: 100, color: "#4AE07A" },
      ],
      dist: [
        { label: "Low", pct: 20 },
        { label: "Mild", pct: 41 },
        { label: "Moderate", pct: 28 },
        { label: "High", pct: 11 },
      ],
      // Population band distribution is bucketed on the RAW GAD-7 score (cutoffs
      // <5 / <10 / <15 / else), best→worst, coloured with higherWorse tone.
      distBands: [
        { label: "High" },
        { label: "Moderate" },
        { label: "Mild" },
        { label: "Low" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Worry & Tension", score: 58 }, { label: "Restlessness", score: 63 },
        { label: "Irritability", score: 62 },
      ],
      module: null,
      waves: Q4.map((l, i) => ({ label: l, score: [57, 59, 60, 61][i] })),
    },

    phq: {
      id: "phq", assessmentId: 5, tab: "Happiness Levels Indicator", name: "Happiness Levels Indicator",
      measures: "Depressive load — low mood, lost interest, and the energy and sleep changes that come with it.",
      recall: "Last 2 weeks", items: 9, n: 330, part: 80,
      score: 63, band: "Mild", trend: { dir: "up", value: 1, improving: true },
      bands: [
        { label: "Severe", lo: 0, hi: 30, color: "#C0392B" }, { label: "Moderately Severe", lo: 30, hi: 47, color: "#FF5C57" },
        { label: "Moderate", lo: 47, hi: 67, color: "#FF8C42" }, { label: "Mild", lo: 67, hi: 85, color: "#FFD84A" },
        { label: "Minimal", lo: 85, hi: 100, color: "#4AE07A" },
      ],
      dist: [
        { label: "Minimal", pct: 22 },
        { label: "Mild", pct: 38 },
        { label: "Moderate", pct: 27 },
        { label: "Moderately Severe", pct: 9 },
        { label: "Severe", pct: 4 },
      ],
      // Population band distribution is bucketed on the RAW PHQ-9 score (cutoffs
      // <5 / <10 / <15 / <20 / else), best→worst, coloured with higherWorse tone.
      distBands: [
        { label: "Severe" },
        { label: "Moderately Severe" },
        { label: "Moderate" },
        { label: "Mild" },
        { label: "Minimal" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Mood & Interest", score: 60 }, { label: "Energy & Sleep", score: 57 },
        { label: "Self-Worth", score: 68 },
      ],
      module: "safety",
      safety: { share: 9, item: "the self-harm item (Q9)", route: "clinical escalation" },
      waves: Q4.map((l, i) => ({ label: l, score: [59, 61, 62, 63][i] })),
    },

    pcl: {
      id: "pcl", assessmentId: 3, tab: "Stress Presence Indicator", name: "Stress Presence Indicator",
      measures: "Post-traumatic stress symptoms — intrusive memories, avoidance, negative shifts in thinking, and being on-edge.",
      recall: "Last month", items: 20, n: 297, part: 72,
      score: 66, band: "Possible", trend: { dir: "up", value: 1, improving: true },
      bands: [
        { label: "Severe", lo: 0, hi: 59, color: "#FF5C57" }, { label: "Likely", lo: 59, hi: 61, color: "#FF8C42" },
        { label: "Possible", lo: 61, hi: 68, color: "#FFD84A" }, { label: "None", lo: 68, hi: 100, color: "#4AE07A" },
      ],
      dist: [
        { label: "None", pct: 55 },
        { label: "Possible", pct: 24 },
        { label: "Likely", pct: 8 },
        { label: "Severe", pct: 13 },
      ],
      // Population band distribution is bucketed on the RAW PCL-5 score (cutoffs
      // <27 / <32 / <34 / else), best→worst, coloured with higherWorse tone.
      distBands: [
        { label: "Severe" },
        { label: "Likely" },
        { label: "Possible" },
        { label: "None" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Intrusion", score: 62 }, { label: "Avoidance", score: 64 },
        { label: "Negative Cognitions", score: 67 }, { label: "Hyperarousal", score: 60 },
      ],
      module: "safety",
      safety: { share: 12, item: "severe-range responses", route: "trauma-informed clinical follow-up" },
      waves: Q4.map((l, i) => ({ label: l, score: [62, 64, 65, 66][i] })),
    },

    mspss: {
      id: "mspss", assessmentId: 8, tab: "Social Support Indicator", name: "Social Support Indicator",
      measures: "How supported people feel — the strength of connection they perceive from partners, family, and friends.",
      recall: "Current", items: 12, n: 346, part: 84,
      score: 74, band: "Strong", trend: { dir: "up", value: 1, improving: true },
      bands: [
        { label: "Low", lo: 0, hi: 33, color: "#FF5C57" }, { label: "Moderate", lo: 33, hi: 68, color: "#FF8C42" },
        { label: "Strong", lo: 68, hi: 100, color: "#4AE07A" },
      ],
      dist: [
        { label: "Low", pct: 9 },
        { label: "Moderate", pct: 34 },
        { label: "Strong", pct: 57 },
      ],
      // Bucketed on the RAW MSPSS score (12–35 low / 36–60 medium / 61+ high),
      // ordered low→high. Positively oriented (higher = more support), so
      // distHigherWorse is false: Low = risk/red, High = good/green.
      distBands: [
        { label: "Low" },
        { label: "Moderate" },
        { label: "Strong" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Significant Other", score: 77 }, { label: "Family", score: 71 },
        { label: "Friends", score: 73 },
      ],
      module: null,
      waves: Q4.map((l, i) => ({ label: l, score: [70, 72, 73, 74][i] })),
    },

    audit: {
      id: "audit", assessmentId: 7, tab: "Alcohol Consumption Indicator", name: "Alcohol Consumption Indicator",
      measures: "Alcohol-related risk — how much and how often people drink, and whether it is starting to cause harm.",
      recall: "Last year", items: 10, n: 313, part: 76,
      score: 78, band: "Low Risk", trend: { dir: "down", value: 1, improving: false },
      bands: [
        { label: "Dependence", lo: 0, hi: 65, color: "#FF5C57" }, { label: "Hazardous", lo: 65, hi: 83, color: "#FF8C42" },
        { label: "Low Risk", lo: 83, hi: 100, color: "#4AE07A" },
      ],
      dist: [
        { label: "Dependence", pct: 8 }, { label: "Hazardous", pct: 27 }, { label: "Low Risk", pct: 65 },
      ],
      dims: [
        { label: "Consumption", score: 76 }, { label: "Dependence Signs", score: 80 },
        { label: "Harm", score: 79 },
      ],
      module: "safety",
      safety: { share: 11, item: "dependence-range responses", route: "clinical / substance-use support" },
      waves: Q4.map((l, i) => ({ label: l, score: [80, 80, 79, 78][i] })),
    },

    proqol: {
      id: "proqol", assessmentId: 6, tab: "Quality of Life Indicator", name: "Quality of Life Indicator",
      measures: "The reward and the cost of the work — satisfaction from helping, set against burnout and the secondary trauma of exposure.",
      recall: "Last 30 days", items: 20, n: 293, part: 71,
      score: 59, band: "Average", trend: { dir: "down", value: 1, improving: false },
      bands: [
        { label: "Strained", lo: 0, hi: 40 }, { label: "Average", lo: 40, hi: 68 },
        { label: "Healthy", lo: 68, hi: 100 },
      ],
      dist: [
        { label: "Strained", pct: 19 }, { label: "Average", pct: 56 }, { label: "Healthy", pct: 25 },
      ],
      // Pooled across the 3 aspects (Low/Medium/High Range on each aspect's raw score).
      // Mixed orientation, so ordered low→high with Low = red … High = green.
      distBands: [
        { label: "Strained" }, { label: "Average" }, { label: "Healthy" },
      ],
      distHigherWorse: false,
      dims: [
        { label: "Compassion Satisfaction", score: 68 }, { label: "Burnout", score: 49 },
        { label: "Secondary Trauma", score: 55 },
      ],
      module: "proqol",
      subscales: [
        { label: "Compassion Satisfaction", score: 68, note: "strength" },
        { label: "Secondary Trauma", score: 55, note: "watch" },
        { label: "Burnout", score: 49, note: "soft spot" },
      ],
      waves: Q4.map((l, i) => ({ label: l, score: [63, 62, 60, 59][i] })),
    },

    vbi: {
      id: "vbi", assessmentId: 39, tab: "Burnout", name: "Burnout",
      measures: "Workplace burnout — exhaustion, disconnection, and erosion of recovery. Here a higher score means MORE burnout.",
      recall: "Current state", items: 40, n: 334, part: 81,
      score: 47, band: "Elevated", higherWorse: true,
      trend: { dir: "up", value: 3, improving: false, note: "higher = more burnout" },
      bands: [
        { label: "Low", lo: 0, hi: 25 }, { label: "Elevated", lo: 25, hi: 50 },
        { label: "Moderate", lo: 50, hi: 70 }, { label: "High", lo: 70, hi: 100 },
      ],
      dist: [
        { label: "Low", pct: 28 }, { label: "Elevated", pct: 34 },
        { label: "Moderate", pct: 26 }, { label: "High", pct: 12 },
      ],
      // Bucketed on evaluated_score (≤25/≤50/≤75/else), best→worst; higher = more burnout.
      distBands: [
        { label: "High" },
        { label: "Moderate" },
        { label: "Elevated" },
        { label: "Low" },
      ],
      distHigherWorse: false,
      // dimensions come live from assessments_multiple_results (evaluated_score per subscale)
      dims: [
        { label: "Emotional Exhaustion", score: 44 }, { label: "Disconnection", score: 58 },
        { label: "Personal Accomplishment", score: 71 }, { label: "Purpose & Meaning", score: 74 },
        { label: "Autonomy & Control", score: 51 }, { label: "Energy & Recovery", score: 41 },
        { label: "Connection & Belonging", score: 69 },
      ],
      module: "vbi",
      pressure: [
        { label: "Recovery Deficit (Silent Risk)", pct: 31 }, { label: "Overload Burnout", pct: 24 },
        { label: "Constraint Burnout", pct: 17 }, { label: "Performance Erosion", pct: 14 },
        { label: "Emotional Withdrawal", pct: 11 },
      ],
      protection: [
        { label: "Engaged Connection", pct: 46 }, { label: "High-Impact Performance", pct: 41 },
        { label: "Aligned Work Experience", pct: 33 }, { label: "Sustainable Performance", pct: 22 },
      ],
      waves: Q4.map((l, i) => ({ label: l, score: [40, 42, 44, 47][i] })),
    },
  },
};

// Per-type index values for the Vasana cross-assessment module (0–100, higher = better)
export const DD_TYPE_INDEX = {
  SA: { Wellness: 64, Anxiety: 55, Support: 64, Burnout: 47, Capability: 77 },
  RE: { Wellness: 71, Anxiety: 66, Support: 85, Burnout: 61, Capability: 74 },
  GA: { Wellness: 62, Anxiety: 59, Support: 71, Burnout: 42, Capability: 73 },
  MA: { Wellness: 60, Anxiety: 52, Support: 72, Burnout: 51, Capability: 68 },
};
export const DD_INDEX_KEYS = ["Wellness", "Anxiety", "Support", "Burnout", "Capability"];

// Forced-choice questions: each option leans toward one type. `qid` is the
// `questions.id` of the Vasana assessment (assessment_id 1); the a/b option order
// matches that row's `markers` column (e.g. qid 101 → markers "Re,Sa").
export const DD_QUESTIONS = [
  { qid: 101, a: { text: "Remain flexible and open", type: "RE" }, b: { text: "Stick to your guns", type: "SA" } },
  { qid: 22, a: { text: "Quality", type: "MA" }, b: { text: "Speed", type: "RE" } },
  { qid: 2, a: { text: "Do the right thing regardless of cost", type: "SA" }, b: { text: "Focus on the number", type: "MA" } },
  { qid: 1, a: { text: "Convert opportunities into reality", type: "GA" }, b: { text: "Find even more opportunities", type: "RE" } },
  { qid: 70, a: { text: "So busy I overlook details", type: "GA" }, b: { text: "Very detail-oriented", type: "MA" } },
  { qid: 21, a: { text: "Fast-paced", type: "RE" }, b: { text: "Orderly and quiet", type: "MA" } },
];
